import type {
  LiveConnectionState,
  LiveCredentials,
  LiveFramePayload,
  LiveParticipant,
  LivePublishOptions,
  LiveTransport,
  LiveTransportHandlers,
} from "../live-transport";
import { mediaSyncRtcConfig } from "../media-sync/media-sync";
import { isPairedDevice } from "../live-attributes";
import { LiveKitAudioTransport } from "./livekit-audio";
import { LiveKitVideoTransport } from "./livekit-video";

/**
 * Le seul fichier du front qui connaisse LiveKit.
 *
 * Tout ce qu'il fait est traduire : les états de `Room` vers
 * `LiveConnectionState`, les `RemoteParticipant` vers `LiveParticipant`, et
 * `publishData` vers `publish`. Aucune décision de jeu ne se prend ici — si
 * quelque chose de musical apparaît dans ce fichier, c'est qu'il est au mauvais
 * endroit.
 *
 * # Import différé
 *
 * `livekit-client` embarque toute la pile WebRTC : plusieurs centaines de
 * kilo-octets, pour une fonctionnalité que la plupart des séances n'ouvrent
 * jamais. Il est donc chargé au premier `connect()`, pas au chargement du
 * module — le studio ne doit pas payer le live pour l'avoir en option.
 *
 * # Le canal de données, pas une piste
 *
 * Les gestes passent par `publishData`, jamais par une piste média. C'est ce
 * que LiveKit prévoit pour ce genre de charge, et surtout ce qui évite toute
 * mise en tampon : une piste audio est découpée en trames avant d'être encodée,
 * un datagramme part tel quel.
 *
 * # La vidéo, elle, passe par des pistes
 *
 * Une image n'a rien à faire sur le canal des gestes. La vidéo part en pistes
 * média sur la même salle, et c'est `LiveKitVideoTransport` qui les tient pour
 * le moteur vidéo (`lib/live/video/`). Un participant qui n'est qu'un appareil
 * appairé n'apparaît jamais dans la liste des participants : il ne joue pas, il
 * n'a pas de lane.
 *
 * # Le son aussi
 *
 * Le son de la séance part de même en pistes, tenues par
 * `LiveKitAudioTransport` pour le moteur audio (`lib/live/audio/`). Ce ne sont
 * pas les notes : le piano voyage en gestes, et chaque poste le rejoue.
 */

/**
 * Sujet des paquets de jeu.
 *
 * LiveKit route par sujet ; le nommer dès maintenant évite d'avoir à
 * distinguer nos trames de celles d'un futur canal — un fil de discussion, une
 * synchronisation d'horloge — après coup, quand deux versions de l'app se
 * parleront déjà.
 */
export const LIVE_PERFORMANCE_TOPIC = "virtuosa.performance";

/**
 * Sujet du datagramme d'amorçage. Personne ne l'écoute : c'est le but, voir
 * `warmUpPublisher`.
 */
export const LIVE_WARMUP_TOPIC = "virtuosa.warmup";

/** Lectures de l'état de séance après la jointure. Voir `readSessionState`. */
const SESSION_STATE_READS = 20;
const SESSION_STATE_READ_INTERVAL_MS = 100;

type LiveKitModule = typeof import("livekit-client");
type LiveKitRoom = InstanceType<LiveKitModule["Room"]>;

/**
 * Le peu qu'on emprunte à l'API **interne** de `livekit-client`.
 *
 * `PCTransport` n'est pas exporté publiquement, et le chemin
 * `room.engine.pcManager` ne fait pas partie de la surface documentée : il est
 * typé public, mais rien ne promet qu'il survivra à une montée de version.
 *
 * Le déclarer ici plutôt que d'importer le type a deux vertus : la dépendance
 * est visible et tient en trois lignes, et le jour où elle casse, c'est ce
 * fichier qui le dit — pas une erreur de compilation à l'autre bout.
 */
type InternalStatsSource = {
  getStats(): Promise<RTCStatsReport> | undefined;
};

type InternalEngine = {
  pcManager?: {
    publisher?: InternalStatsSource;
    subscriber?: InternalStatsSource;
  };
};

export class LiveKitTransport implements LiveTransport {
  private room: LiveKitRoom | null = null;
  private handlers: LiveTransportHandlers | null = null;
  private livekit: LiveKitModule | null = null;

  /** La vidéo de la séance, sur la même salle. Voir `LiveKitVideoTransport`. */
  readonly video = new LiveKitVideoTransport();

  /** Le son de la séance, sur la même salle. Voir `LiveKitAudioTransport`. */
  readonly audio = new LiveKitAudioTransport();

  async connect(credentials: LiveCredentials, handlers: LiveTransportHandlers): Promise<void> {
    const livekit = await import("livekit-client");

    const room = new livekit.Room({
      // `adaptiveStream` choisit la couche d'après l'élément qui affiche la
      // piste, et ne le voit que si la piste passe par `track.attach()`. Les
      // nôtres passent par un `MediaStream` : la couche se demande donc à
      // l'abonnement. `dynacast`, lui, coupe chez l'émetteur les couches que
      // personne ne reçoit.
      adaptiveStream: false,
      dynacast: true,
    });

    this.room = room;
    this.handlers = handlers;
    this.livekit = livekit;

    this.bind(livekit, room, handlers);
    this.video.bind(livekit, room);
    this.audio.bind(livekit, room);

    // Avant la connexion : sous Chromium, c'est elle qui ouvre les flux encodés
    // que la vidéo et le son traversent pour se caler sur les notes.
    await room.connect(credentials.serverUrl, credentials.token, { rtcConfig: mediaSyncRtcConfig() });

    this.warmUpPublisher(room);
    handlers.onState("connected");
    this.publishParticipants();

    void this.readSessionState(room, handlers);
  }

  /**
   * Fait négocier le canal émetteur tout de suite, plutôt qu'à la première note.
   *
   * En mode « abonné d'abord », le SDK ne négocie la connexion émettrice qu'au
   * premier `publishData` : ICE, DTLS, l'ouverture du canal — cent à trois
   * cents millisecondes que payait la première note de chaque séance, qui
   * arrivait en retard ou pas du tout. Un datagramme d'un octet sur un sujet
   * que personne ne lit déclenche la même négociation, à un moment où elle ne
   * coûte à personne. Le retour n'est pas attendu : s'il échoue, la première
   * note fera la négociation, comme avant.
   */
  private warmUpPublisher(room: LiveKitRoom): void {
    void room.localParticipant
      .publishData(new Uint8Array([0]), { reliable: false, topic: LIVE_WARMUP_TOPIC })
      .catch(() => {
        // Silencieux à dessein, cf. ci-dessus.
      });
  }

  /**
   * Lit l'état de séance déjà présent dans la salle, en insistant un peu.
   *
   * # Pourquoi une relance, et non une simple lecture
   *
   * `RoomEvent.RoomMetadataChanged` n'est émis que si une valeur *précédente*
   * existait :
   *
   * ```js
   * if (oldRoom && oldRoom.metadata !== room.metadata) { … }
   * ```
   *
   * Au premier `handleRoomUpdate` — celui de la réponse de jointure —
   * `oldRoom` est `undefined`, donc **aucun événement**. Et la métadonnée d'une
   * séance déjà lancée ne change plus : plus rien n'arrive ensuite. Tout repose
   * donc sur `room.metadata` peuplé au retour de `connect()`, ce que le SDK ne
   * promet nulle part — et il ne l'était pas.
   *
   * On relit donc quelques fois, brièvement. La lecture est locale et gratuite ;
   * on s'arrête au premier état non vide, ou au bout de la fenêtre — une salle
   * sans état est un cas légitime, pas une erreur à réessayer indéfiniment.
   */
  private async readSessionState(
    room: LiveKitRoom,
    handlers: LiveTransportHandlers,
  ): Promise<void> {
    for (let attempt = 0; attempt < SESSION_STATE_READS; attempt += 1) {
      const metadata = room.metadata ?? "";

      if (metadata !== "") {
        handlers.onSessionState(metadata);

        return;
      }

      if (this.room !== room) {
        return;
      }

      await new Promise((resolve) => setTimeout(resolve, SESSION_STATE_READ_INTERVAL_MS));
    }
  }

  /**
   * L'aller-retour jusqu'au SFU, lu dans les statistiques ICE.
   *
   * # Pourquoi `candidate-pair` et non `remote-inbound-rtp`
   *
   * Le second vient des rapports RTCP d'une session RTP, donc **d'une piste
   * média** — et on n'en publie aucune, tout passe par le canal de données. Le
   * premier vient des échanges STUN de la couche ICE : il existe dès qu'il y a
   * un transport, avec ou sans média.
   *
   * # Pourquoi la paire *sélectionnée*
   *
   * `nominated` peut rester vrai sur plusieurs paires au fil d'une connexion —
   * après un redémarrage ICE, les anciennes gardent le drapeau. Seul
   * `selectedCandidatePairId`, publié par la statistique `transport`, désigne
   * celle qui sert en ce moment.
   *
   * # La fraîcheur
   *
   * `currentRoundTripTime` est rafraîchi au rythme des vérifications de
   * consentement STUN, pas en continu : quelques secondes entre deux valeurs.
   * C'est assez pour un affichage de santé et pour composer les jambes, pas
   * pour une mesure fine — celle-là se fait contre le service de temps.
   */
  async localRoundTripMs(): Promise<number | null> {
    const engine = (this.room as unknown as { engine?: InternalEngine } | null)?.engine;
    const manager = engine?.pcManager;
    const transports = [manager?.publisher, manager?.subscriber].filter(
      (transport): transport is InternalStatsSource => transport !== undefined,
    );

    for (const transport of transports) {
      const rtt = await readSelectedPairRtt(transport);

      if (rtt !== null) {
        return rtt;
      }
    }

    return null;
  }

  publish(payload: LiveFramePayload, options: LivePublishOptions): void {
    const room = this.room;

    if (room === null || room.state !== "connected") {
      return;
    }

    // `publishData` rend une promesse ; on ne l'attend pas. Attendre
    // sérialiserait les envois derrière le tour de boucle, ce qui est
    // exactement le retard qu'on cherche à ne pas prendre. Un échec sur un
    // datagramme de jeu n'appelle aucune reprise : la note suivante arrive.
    void room.localParticipant
      .publishData(payload, {
        reliable: options.reliable,
        topic: LIVE_PERFORMANCE_TOPIC,
      })
      .catch(() => {
        // Silencieux à dessein, cf. ci-dessus.
      });
  }

  async disconnect(): Promise<void> {
    const room = this.room;

    this.room = null;
    this.handlers = null;
    this.video.unbind();
    this.audio.unbind();

    if (room === null) {
      return;
    }

    room.removeAllListeners();
    await room.disconnect();
  }

  private bind(livekit: LiveKitModule, room: LiveKitRoom, handlers: LiveTransportHandlers): void {
    const { RoomEvent } = livekit;

    room.on(RoomEvent.DataReceived, (payload, participant, _kind, topic) => {
      // Sans participant, le paquet vient de l'API serveur : c'est le service
      // qui parle — le fil de la salle, les réactions —, jamais un pair. Un pair
      // qui écrirait sur ces sujets est ignoré : le service seul les signe.
      if (participant === undefined) {
        if (topic !== undefined && topic !== LIVE_PERFORMANCE_TOPIC) {
          handlers.onServerData?.(topic, payload);
        }

        return;
      }

      if (topic !== LIVE_PERFORMANCE_TOPIC) {
        return;
      }

      handlers.onData(payload, toLiveParticipant(participant.identity, participant.name));
    });

    room.on(RoomEvent.RoomMetadataChanged, (metadata) => handlers.onSessionState(metadata));

    room.on(RoomEvent.ParticipantConnected, () => this.publishParticipants());
    room.on(RoomEvent.ParticipantDisconnected, () => this.publishParticipants());
    // L'attribut qui fait d'un participant un appareil appairé arrive avec lui ;
    // le relire à chaque changement coûte une liste, et protège d'un attribut
    // posé après coup.
    room.on(RoomEvent.ParticipantAttributesChanged, () => this.publishParticipants());

    room.on(RoomEvent.Reconnecting, () => handlers.onState("reconnecting"));
    room.on(RoomEvent.Reconnected, () => {
      handlers.onState("connected");
      this.publishParticipants();
    });

    room.on(RoomEvent.Disconnected, (reason) => {
      // Sorti par le serveur — l'exclusion — et non par la fin de la salle ni
      // par la liaison : le studio doit le savoir avant de lire « fermé ».
      if (reason === livekit.DisconnectReason.PARTICIPANT_REMOVED) {
        handlers.onEvicted?.();
      }

      this.video.unbind();
      this.audio.unbind();
      handlers.onParticipants([]);
      handlers.onState("closed");
    });
  }

  private publishParticipants(): void {
    const room = this.room;
    const handlers = this.handlers;

    if (room === null || handlers === null) {
      return;
    }

    handlers.onParticipants(
      Array.from(room.remoteParticipants.values())
        .filter((participant) => !isPairedDevice(participant.attributes))
        .map((participant) => toLiveParticipant(participant.identity, participant.name)),
    );
  }

}

function toLiveParticipant(identity: string, name: string | undefined): LiveParticipant {
  return {
    id: identity,
    // Le nom vient du jeton, que l'API signe avec un repli non vide. La garde
    // couvre le cas d'un participant qui ne serait pas passé par notre API.
    name: name === undefined || name === "" ? "Invité" : name,
  };
}

/** Vrai si l'état correspond à une session utilisable. */
export function isLiveConnected(state: LiveConnectionState): boolean {
  return state === "connected";
}

/**
 * L'aller-retour de la paire de candidats actuellement retenue, en
 * millisecondes. `null` si le transport n'a pas encore de statistiques.
 */
async function readSelectedPairRtt(transport: InternalStatsSource): Promise<number | null> {
  // `getStats()` peut rendre `undefined` — la méthode elle-même, pas seulement
  // la promesse — tant que la connexion n'est pas établie. C'est exactement le
  // cas au démarrage, quand on voudra l'appeler.
  const report = await transport.getStats();

  if (report === undefined) {
    return null;
  }

  let selectedPairId: string | undefined;

  report.forEach((stat) => {
    if (stat.type === "transport" && typeof stat.selectedCandidatePairId === "string") {
      selectedPairId = stat.selectedCandidatePairId;
    }
  });

  if (selectedPairId === undefined) {
    return null;
  }

  const pair = report.get(selectedPairId);

  if (pair?.type !== "candidate-pair" || typeof pair.currentRoundTripTime !== "number") {
    return null;
  }

  return pair.currentRoundTripTime * 1000;
}
